/**
 * AdminReadinessBanner — sticky red strip shown on every admin page when
 * critical readiness checks are failing.
 *
 * Why per-page: the 2026-05-22 customer loss happened because nobody saw
 * the test-key state until it was too late. Surfacing the failure on
 * every admin surface (Funnel, Conversions, Users, Broadcast) means an
 * admin literally cannot ignore it during normal day-to-day work.
 *
 * The component self-fetches /api/admin/prod-readiness once on mount.
 * Silently no-op if the user isn't authenticated as admin (the endpoint
 * 403s, we catch and hide).
 *
 * Auto-rechecks every 5 minutes so a fix is reflected quickly.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiBase';

const POLL_MS = 5 * 60 * 1000;

const AdminReadinessBanner = () => {
  const [status, setStatus] = useState(null);
  const [criticalCount, setCriticalCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = () => {
      axios
        .get(`${API}/admin/prod-readiness`, { withCredentials: true })
        .then((res) => {
          if (cancelled) return;
          setStatus(res.data.status);
          setCriticalCount(res.data.critical_failures || 0);
        })
        .catch(() => { /* not admin / not authed → silently hide */ });
    };
    fetchStatus();
    const id = window.setInterval(fetchStatus, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Only render when there's a confirmed critical failure
  if (status !== 'fail' || criticalCount === 0 || dismissed) return null;

  return (
    <div
      className="sticky top-0 z-40 bg-[#7f1d1d] text-white"
      data-testid="admin-readiness-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
        <ShieldAlert size={16} className="flex-shrink-0 text-[#fbbf24]" />
        <span className="text-xs sm:text-sm font-semibold flex-1 min-w-0">
          PRODUCTION READINESS:{' '}
          <strong>{criticalCount} critical issue{criticalCount !== 1 ? 's' : ''} detected</strong>
          {' '}— do not deploy until resolved.
        </span>
        <Link
          to="/admin/prod-readiness"
          className="inline-flex items-center gap-1 px-2.5 py-1 bg-white text-[#7f1d1d] text-xs font-bold hover:bg-white/90 transition-colors"
          data-testid="banner-view-link"
        >
          View report <ArrowRight size={11} />
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-white/10 transition-colors"
          aria-label="Dismiss for this session"
          data-testid="banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default AdminReadinessBanner;
